import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { Globe, MoreHorizontal, LogOut, MessageSquare } from 'lucide-react';
import './App.css';
import './theme.css';

import BottomNav from './components/BottomNav';
import Feed from './components/Feed';
import ReelsFeed from './components/ReelsFeed';
import CreatePost from './components/CreatePost';
import Notifications from './components/Notifications';
import Explore from './components/Explore';
import HashtagPage from './components/HashtagPage';
import Profile from './components/Profile';
import ProfileSettings from './components/ProfileSettings';
import Conversations from './components/Conversations';
import PrivateChat from './components/PrivateChat';
import BlockedUsersScreen from './components/BlockedUsersScreen';
import AdminReports from './components/AdminReports';
import AdminReportDetail from './components/AdminReportDetail';
import InstallPrompt from './components/InstallPrompt';
import useEventStream from './hooks/useEventStream';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ==================== Auth Screen ====================

const AuthScreen = ({ onAuthed }) => {
  const { t, isRTL, language, toggleLanguage } = useLanguage();
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [gender, setGender] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const path = mode === 'login' ? '/auth/login' : '/auth/register';
      const body =
        mode === 'login'
          ? { email, password }
          : { email, password, name, gender: gender || null, language };
      const res = await axios.post(`${API}${path}`, body);
      const { access_token, user } = res.data;
      localStorage.setItem('token', access_token);
      onAuthed(access_token, user);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = t('error');
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail))
        msg = detail.map((d) => d?.msg || JSON.stringify(d)).join(', ');
      else if (detail) msg = JSON.stringify(detail);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      dir={isRTL ? 'rtl' : 'ltr'}
      className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center px-4"
    >
      <div className="w-full max-w-md">
        <div className="flex justify-end mb-4">
          <button
            onClick={toggleLanguage}
            data-testid="auth-lang-toggle"
            className="px-3 py-1.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium hover:bg-purple-200"
          >
            {language === 'ar' ? 'English' : 'العربية'}
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-7">
          <div className="text-center mb-6">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-tr from-purple-500 via-fuchsia-500 to-pink-500 flex items-center justify-center text-white text-2xl font-black mb-3 shadow-lg">
              ◎
            </div>
            <h1
              data-testid="brand-name"
              className="text-3xl font-extrabold bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-transparent"
            >
              {t('appName')}
            </h1>
            <p className="text-sm text-gray-500 mt-1">{t('tagline')}</p>
          </div>

          <div className="flex gap-2 mb-5 bg-gray-100 rounded-xl p-1">
            <button
              onClick={() => setMode('login')}
              data-testid="auth-tab-login"
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${
                mode === 'login' ? 'bg-white text-purple-700 shadow' : 'text-gray-700'
              }`}
            >
              {t('login')}
            </button>
            <button
              onClick={() => setMode('register')}
              data-testid="auth-tab-register"
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${
                mode === 'register' ? 'bg-white text-purple-700 shadow' : 'text-gray-700'
              }`}
            >
              {t('register')}
            </button>
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === 'register' && (
              <>
                <div>
                  <label htmlFor="auth-name-input" className="text-xs font-medium text-gray-600 mb-1 block">{t('name')}</label>
                  <input
                    id="auth-name-input"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    data-testid="auth-name-input"
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:border-purple-400"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setGender('male')}
                    data-testid="auth-gender-male"
                    aria-pressed={gender === 'male'}
                    className={`flex-1 py-2 rounded-lg border text-sm ${
                      gender === 'male'
                        ? 'border-blue-400 bg-blue-50 text-blue-700'
                        : 'border-gray-200 text-gray-700'
                    }`}
                  >
                    {language === 'ar' ? 'ذكر' : 'Male'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setGender('female')}
                    data-testid="auth-gender-female"
                    aria-pressed={gender === 'female'}
                    className={`flex-1 py-2 rounded-lg border text-sm ${
                      gender === 'female'
                        ? 'border-pink-400 bg-pink-50 text-pink-700'
                        : 'border-gray-200 text-gray-700'
                    }`}
                  >
                    {language === 'ar' ? 'أنثى' : 'Female'}
                  </button>
                </div>
              </>
            )}
            <div>
              <label htmlFor="auth-email-input" className="text-xs font-medium text-gray-600 mb-1 block">{t('email')}</label>
              <input
                id="auth-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="auth-email-input"
                placeholder="example@email.com"
                autoComplete="email"
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:border-purple-400"
              />
            </div>
            <div>
              <label htmlFor="auth-password-input" className="text-xs font-medium text-gray-600 mb-1 block">{t('password')}</label>
              <input
                id="auth-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                data-testid="auth-password-input"
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:border-purple-400"
              />
            </div>

            {error && (
              <p data-testid="auth-error" role="alert" className="text-sm text-red-600">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="auth-submit"
              className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 text-white font-semibold hover:opacity-95 disabled:opacity-50 transition-opacity"
            >
              {submitting ? t('loading') : mode === 'login' ? t('login') : t('register')}
            </button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-4">
            {mode === 'login' ? t('noAccount') : t('haveAccount')}{' '}
            <button
              onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
              data-testid="auth-toggle-mode"
              className="text-purple-600 font-semibold hover:underline"
            >
              {mode === 'login' ? t('register') : t('login')}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

// ==================== Top Header ====================

const TopHeader = ({ user, t, isRTL, language, toggleLanguage, onLogout, onOpenMessages, unreadChat = 0 }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuWrapRef = React.useRef(null);
  const menuTriggerRef = React.useRef(null);

  // Phase 5 polish — parity with SafetyMenu: Escape + outside-click close,
  // Escape returns focus to the trigger.
  React.useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (!menuWrapRef.current) return;
      if (!menuWrapRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        menuTriggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  return (
    <header
      dir={isRTL ? 'rtl' : 'ltr'}
      className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-gray-100"
    >
      <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
        <h1
          data-testid="header-brand"
          className="text-xl font-extrabold bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-transparent"
        >
          {t('appName')}
        </h1>
        <div className="flex items-center gap-1">
          <button
            onClick={onOpenMessages}
            data-testid="header-messages"
            className="relative p-2 rounded-full hover:bg-gray-100 text-gray-600"
            aria-label={`${t('messages')}${unreadChat > 0 ? ` (${unreadChat})` : ''}`}
          >
            <MessageSquare className="w-5 h-5" aria-hidden="true" />
            {unreadChat > 0 && (
              <span
                data-testid="header-messages-badge"
                aria-hidden="true"
                className="absolute -top-0.5 -end-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none"
              >
                {unreadChat > 99 ? '99+' : unreadChat}
              </span>
            )}
          </button>
          <button
            onClick={toggleLanguage}
            data-testid="header-lang-toggle"
            className="p-2 rounded-full hover:bg-gray-100 text-gray-600"
            aria-label={t('languageToggle')}
          >
            <Globe className="w-5 h-5" aria-hidden="true" />
          </button>
          <div ref={menuWrapRef} className="relative">
            <button
              ref={menuTriggerRef}
              onClick={() => setMenuOpen((v) => !v)}
              data-testid="header-menu"
              className="p-2 rounded-full hover:bg-gray-100 text-gray-600"
              aria-label={menuOpen ? t('closeMenu') : t('openMenu')}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <MoreHorizontal className="w-5 h-5" aria-hidden="true" />
            </button>
            {menuOpen && (
              <div
                role="menu"
                aria-label={t('moreActions')}
                className={`absolute mt-2 ${
                  isRTL ? 'left-0' : 'right-0'
                } w-44 bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden`}
              >
                <button
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onLogout();
                  }}
                  data-testid="menu-logout"
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-red-600 hover:bg-red-50"
                >
                  <LogOut className="w-4 h-4" aria-hidden="true" />
                  <span>{t('logout')}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

// ==================== Main shell ====================

function AppContent() {
  const { t, isRTL, language, toggleLanguage, setLanguage } = useLanguage();
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('feed'); // feed|explore|reels|create|notifications|profile
  const [showSettings, setShowSettings] = useState(false);
  const [viewingUserId, setViewingUserId] = useState(null);
  const [feedRefreshKey, setFeedRefreshKey] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showMessages, setShowMessages] = useState(false);
  const [chatWith, setChatWith] = useState(null); // user_id of open thread
  const [unreadChat, setUnreadChat] = useState(0);
  const [showBlocked, setShowBlocked] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [adminReportDetail, setAdminReportDetail] = useState(null);
  const [viewingHashtag, setViewingHashtag] = useState(null);

  const refreshUnread = useCallback(async () => {
    if (!token) return;
    try {
      const [n, c] = await Promise.all([
        axios.get(`${API}/notifications/unread-count`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/chat/unread-count`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setUnreadCount(n.data?.unread_count || 0);
      setUnreadChat(c.data?.unread_count || 0);
    } catch {
      // ignore
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setUnreadCount(0);
      setUnreadChat(0);
      return;
    }
    // One initial fetch so the badge is correct immediately on load /
    // login. Subsequent updates are pushed via SSE (see below).
    refreshUnread();
  }, [token, refreshUnread]);

  // Phase 19 — Server-Sent Events replace the 30 s polling loop.
  // The hook returns the most recent event; we apply unread_counts
  // updates directly and re-fetch on `notification` / `message_new`
  // pushes so any other component reading the same cache stays fresh.
  const { lastEvent: liveEvent, status: liveStatus } = useEventStream(token);

  useEffect(() => {
    if (!liveEvent) return;
    if (liveEvent.type === 'unread_counts') {
      const d = liveEvent.data || {};
      if (typeof d.notifications_unread === 'number') {
        setUnreadCount(d.notifications_unread);
      }
      if (typeof d.chat_unread === 'number') {
        setUnreadChat(d.chat_unread);
      }
    }
  }, [liveEvent]);

  // Safety net: if the stream is down (8+ reconnects in 60 s) or the
  // tab was hidden for a while, force a one-shot recount on next
  // visibility-change to ``visible``.
  useEffect(() => {
    if (!token) return undefined;
    let hiddenSince = null;
    const handler = () => {
      if (document.visibilityState === 'hidden') {
        hiddenSince = Date.now();
        return;
      }
      // visible
      const wasHiddenLong = hiddenSince && Date.now() - hiddenSince > 60_000;
      hiddenSince = null;
      if (wasHiddenLong || liveStatus === 'down') {
        refreshUnread();
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [token, refreshUnread, liveStatus]);

  useEffect(() => {
    // theme based on gender (kept from original behavior)
    const theme =
      user?.gender === 'female' ? 'pink' : user?.gender === 'male' ? 'blue' : 'purple';
    document.documentElement.setAttribute('data-theme', theme);
  }, [user]);

  const fetchMe = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(res.data);
    } catch {
      // invalid token
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    }
  }, [token]);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const handleAuthed = (newToken, newUser) => {
    setToken(newToken);
    setUser(newUser);
    setActiveTab('feed'); // social-first landing
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setActiveTab('feed');
    setShowSettings(false);
    setShowMessages(false);
    setChatWith(null);
    setViewingUserId(null);
    setShowBlocked(false);
    setShowAdmin(false);
    setAdminReportDetail(null);
    setViewingHashtag(null);
  };

  if (!token || !user) {
    return <AuthScreen onAuthed={handleAuthed} />;
  }

  // Chat overlays
  if (chatWith) {
    return (
      <PrivateChat
        token={token}
        API={API}
        user={user}
        otherUserId={chatWith}
        t={t}
        isRTL={isRTL}
        onBack={() => {
          setChatWith(null);
          refreshUnread();
        }}
        onOpenProfile={(uid) => {
          setChatWith(null);
          if (uid && uid !== user.id) setViewingUserId(uid);
        }}
        onAfterRead={refreshUnread}
        liveEvent={liveEvent}
      />
    );
  }

  if (showMessages) {
    return (
      <Conversations
        token={token}
        API={API}
        t={t}
        isRTL={isRTL}
        onBack={() => setShowMessages(false)}
        onOpenThread={(uid) => {
          setShowMessages(false);
          setChatWith(uid);
        }}
        onAfterRead={refreshUnread}
        liveEvent={liveEvent}
      />
    );
  }

  if (viewingHashtag) {
    return (
      <HashtagPage
        token={token}
        API={API}
        t={t}
        isRTL={isRTL}
        tag={viewingHashtag}
        onBack={() => setViewingHashtag(null)}
        onOpenPost={() => setViewingHashtag(null)}
      />
    );
  }

  // Settings overlay (own profile only)
  if (showBlocked) {
    return (
      <BlockedUsersScreen
        token={token}
        API={API}
        t={t}
        isRTL={isRTL}
        onBack={() => setShowBlocked(false)}
      />
    );
  }

  if (adminReportDetail) {
    return (
      <AdminReportDetail
        token={token}
        API={API}
        t={t}
        isRTL={isRTL}
        report={adminReportDetail}
        onBack={() => setAdminReportDetail(null)}
        onUpdated={() => { /* list refetches on tab return via showAdmin remount */ }}
      />
    );
  }

  if (showAdmin) {
    return (
      <AdminReports
        token={token}
        API={API}
        t={t}
        isRTL={isRTL}
        onBack={() => setShowAdmin(false)}
        onOpenDetail={(r) => setAdminReportDetail(r)}
      />
    );
  }

  if (showSettings) {
    return (
      <ProfileSettings
        isRTL={isRTL}
        user={user}
        token={token}
        API={API}
        language={language}
        setLanguage={setLanguage}
        onBack={() => setShowSettings(false)}
        onUserUpdated={(u) => setUser((prev) => ({ ...(prev || {}), ...u }))}
        onLogout={handleLogout}
        onOpenBlocked={() => setShowBlocked(true)}
        onOpenAdmin={() => setShowAdmin(true)}
        t={t}
      />
    );
  }

  // Other-user profile overlay
  if (viewingUserId && viewingUserId !== user.id) {
    return (
      <Profile
        isRTL={isRTL}
        user={user}
        token={token}
        API={API}
        viewUserId={viewingUserId}
        onBack={() => setViewingUserId(null)}
        onOpenProfile={(uid) => {
          if (uid === user.id) {
            setViewingUserId(null);
            setActiveTab('profile');
          } else {
            setViewingUserId(uid);
          }
        }}
        onMessage={(uid) => {
          setViewingUserId(null);
          setChatWith(uid);
        }}
        t={t}
      />
    );
  }

  // Main shell with bottom nav
  let body = null;
  switch (activeTab) {
    case 'feed':
      body = (
        <Feed
          token={token}
          API={API}
          currentUser={user}
          t={t}
          isRTL={isRTL}
          onOpenProfile={(uid) => {
            if (uid === user.id) setActiveTab('profile');
            else setViewingUserId(uid);
          }}
          onGoToExplore={() => setActiveTab('explore')}
          refreshKey={feedRefreshKey}
        />
      );
      break;
    case 'explore':
      body = (
        <Explore
          token={token}
          API={API}
          t={t}
          isRTL={isRTL}
          currentUser={user}
          onOpenProfile={(uid) => {
            if (uid === user.id) setActiveTab('profile');
            else setViewingUserId(uid);
          }}
          onOpenHashtag={(tag) => setViewingHashtag(tag)}
          onOpenPost={() => setActiveTab('feed')}
        />
      );
      break;
    case 'create':
      body = (
        <CreatePost
          token={token}
          API={API}
          t={t}
          isRTL={isRTL}
          onCreated={() => {
            setFeedRefreshKey((k) => k + 1);
            setActiveTab('feed');
          }}
          onCancel={() => setActiveTab('feed')}
        />
      );
      break;
    case 'reels':
      body = (
        <ReelsFeed
          token={token}
          t={t}
          isRTL={isRTL}
          user={user}
          onOpenProfile={(uid) => {
            if (uid === user.id) setActiveTab('profile');
            else setViewingUserId(uid);
          }}
        />
      );
      break;
    case 'notifications':
      body = (
        <Notifications
          token={token}
          API={API}
          t={t}
          isRTL={isRTL}
          onOpenProfile={(uid) => {
            if (uid === user.id) setActiveTab('profile');
            else setViewingUserId(uid);
          }}
          onAfterRead={() => setUnreadCount(0)}
          liveEvent={liveEvent}
        />
      );
      break;
    case 'profile':
      body = (
        <>
          <Profile
            isRTL={isRTL}
            user={user}
            token={token}
            API={API}
            viewUserId={null}
            onBack={() => setActiveTab('feed')}
            onOpenSettings={() => setShowSettings(true)}
            onOpenProfile={(uid) => {
              if (uid === user.id) return;
              setViewingUserId(uid);
            }}
            onCreatePost={() => setActiveTab('create')}
            t={t}
          />
          <InstallPrompt />
        </>
      );
      break;
    default:
      body = null;
  }

  // Explore + Profile + Reels components have their own full-screen chrome;
  // for those tabs, hide our top header (Reels is full-bleed black video).
  const hideTopHeader = activeTab === 'explore' || activeTab === 'profile' || activeTab === 'reels';

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className="min-h-screen bg-gray-50 safe-pb-nav">
      {/* Phase 5 — keyboard skip link (becomes visible on focus only). */}
      <a
        href="#main-content"
        className="sr-only-circleo focus:not-sr-only focus:absolute focus:top-2 focus:start-2 focus:z-50 focus:bg-purple-600 focus:text-white focus:px-3 focus:py-2 focus:rounded-lg focus:text-sm focus:font-semibold"
        data-testid="skip-to-content"
      >
        {t('skipToContent')}
      </a>
      {!hideTopHeader && (
        <TopHeader
          user={user}
          t={t}
          isRTL={isRTL}
          language={language}
          toggleLanguage={toggleLanguage}
          onLogout={handleLogout}
          onOpenMessages={() => setShowMessages(true)}
          unreadChat={unreadChat}
        />
      )}
      <main id="main-content" data-testid="main-content">{body}</main>
      <BottomNav
        active={activeTab}
        onChange={setActiveTab}
        t={t}
        isRTL={isRTL}
        unreadCount={unreadCount}
      />
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
}

export default App;
